import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import {
  catchError,
  map,
  Observable,
  shareReplay,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { SeminarDateSeriesListModel } from '../../../models/seminar.model';
import { TitleCardModel } from '../../../models/title-card.model';
import { UserModel } from '../../../models/user.model';
import { AlertService } from '../../../services/alert.service';
import { SeminarService } from '../../../services/seminar.service';
import { UserService } from '../../../services/user.service';
import {
  getSeminarFobiPoints,
  getSeminarLecturerText,
} from '../../../utils/seminar.utils';
import { getUserFullName } from '../../../utils/user.utils';

@Component({
  selector: 'app-lecturer-seminars',
  templateUrl: './lecturer-seminars.component.html',
  styleUrl: './lecturer-seminars.component.scss',
  standalone: false,
})
export class LecturerSeminarsComponent implements OnInit, OnDestroy {
  public dataSource$: Observable<any[]>;
  public seminarsLoading = true;
  public lecturerLoading = true;
  public currentView: 'grid' | 'list' = 'list';
  public titleCard: TitleCardModel;
  public lecturer: UserModel;

  private destroy$ = new Subject<void>();

  constructor(
    private seminarService: SeminarService,
    private userService: UserService,
    public location: Location,
    private activatedRoute: ActivatedRoute,
    private alertService: AlertService
  ) {}

  public ngOnInit(): void {
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const id = params.get('id');
        if (id) {
          this.seminarsLoading = true;
          this.lecturerLoading = true;
          this.loadLecturer(+atob(id));
        }
      });
  }

  private loadLecturer(lecturerId: number): void {
    this.userService
      .getLecturerDetails(lecturerId)
      .pipe(take(1))
      .subscribe({
        next: lecturer => {
          this.lecturer = lecturer;
          this.titleCard = {
            title: `Seminare von ${getUserFullName(lecturer)}`,
            descriptionBold: lecturer.jobTitle ?? '',
            description: '',
            image: null,
            color0: '#0E332E',
            color50: '#0E332E',
            color70: '#BBD1CE',
            color80: '#E8F0EF',
          };
          this.lecturerLoading = false;
          this.getSeminars(lecturerId);
        },
        error: () => {
          this.lecturerLoading = false;
          this.alertService.showErrorAlert(
            'Der Dozent konnte nicht geladen werden.'
          );
        },
      });
  }

  public changeView(view: 'grid' | 'list'): void {
    this.currentView = view;
  }

  private getSeminars(lecturerId: number): void {
    this.dataSource$ = this.seminarService
      .getSeminarsByLecturer(lecturerId)
      .pipe(
        take(1),
        map(seminarDateSeriesList => {
          this.seminarsLoading = false;
          return seminarDateSeriesList.map(seminarDateSeries => ({
            ...seminarDateSeries,
            title: seminarDateSeries.seminar.title,
            dates: seminarDateSeries.dates,
            lecturer: getSeminarLecturerText(seminarDateSeries),
            fobiPoints: getSeminarFobiPoints(seminarDateSeries),
            status: this.getSeminarStatus(seminarDateSeries),
          }));
        }),
        catchError(error => {
          this.seminarsLoading = false;
          console.error('Error fetching seminars', error);
          this.alertService.showErrorAlert(
            'Die Seminare konnten nicht geladen werden.'
          );
          return [];
        }),
        shareReplay(1)
      );
  }

  private getSeminarStatus(
    seminarDateSeries: SeminarDateSeriesListModel
  ): string {
    if (
      seminarDateSeries.dates.find(item =>
        moment(item.startDate).isBefore(moment())
      ) &&
      seminarDateSeries.dates.find(item =>
        moment(item.endDate).isAfter(moment())
      )
    ) {
      return 'Läuft';
    }
    if (seminarDateSeries.seminar.isCanceled) {
      return 'Abgesagt';
    }
    return `${Math.max(0, seminarDateSeries.seminar.maxParticipants - seminarDateSeries.participantCount)} frei`;
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
